import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PhoneInputWithCountrySelect, {
  isValidPhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomButton from "../../../components/CustomButton/CustomButton";
import { instructorUpdateProfileAPI } from "../../../redux/features/intructorSlice";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { GoogleMap, useLoadScript, Autocomplete } from "@react-google-maps/api";
import DottedLoading from "../../../components/DottedLoading/DottedLoading";
import { format } from "date-fns";
import PhoneInput from "react-phone-input-2";
import { getLoginResponseAPI } from "../../../redux/features/authSlice";

// Define libraries needed for Google Maps
const libraries = ["places"];

const PlaceAutocomplete = ({ setFieldValue }) => {
  const autocompleteRef = useRef(null);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.address_components) {
      let location = place.formatted_address || "";
      let latitude = place.geometry?.location?.lat() || null;
      let longitude = place.geometry?.location?.lng() || null;

      setFieldValue("location", location);
      setFieldValue("latitude", latitude);
      setFieldValue("longitude", longitude);
    }
  };

  return (
    <div className="mb-12 mb-md-16">
      <label
        htmlFor="autocomplete"
        className="form-label black-100 fw-semibold mb-8 mb-lg-12"
      >
        Location
      </label>
      <Autocomplete
        onLoad={(autocomplete) => {
          autocompleteRef.current = autocomplete;
        }}
        onPlaceChanged={onPlaceChanged}
        fields={["address_components", "formatted_address", "geometry"]}
      >
        <Field
          type="text"
          id="autocomplete"
          name="location"
          className="form-control"
          placeholder="Enter a location"
          onChange={(e) => setFieldValue("location", e.target.value)}
        />
      </Autocomplete>
      <ErrorMessage
        name="location"
        component="div"
        className="text-danger error"
      />
    </div>
  );
};

export default function ProfileSetup() {
  const today = new Date();
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [defaultCountry, setDefaultCountry] = useState("US");

  // Load Google Maps script with Places library
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const response = await fetch(
          "https://api.ipgeolocation.io/ipgeo?apiKey=a648d260fc9d43dfa2a8ec35e0572235"
        );
        const data = await response.json();
        if (data?.country_code2) {
          setDefaultCountry(data.country_code2.toLowerCase());
        }
      } catch (error) {
        console.error("Failed to fetch country from IP:", error);
        // Fallback to "US" if API fails
      }
    };
    fetchCountry();
  }, []);

  const initialValues = {
    full_name: "",
    location: "",
    phone_number: "",
    date_of_birth: "",
    bio: "",
    latitude: null,
    longitude: null,
  };

  const validationSchema = Yup.object({
    full_name: Yup.string().required("Name is required"),
    location: Yup.string().required("Location is required"),
    phone_number: Yup.string()
      .required("Phone number is required")
      .test("is-valid-phone", "Invalid phone number", (value) => {
        if (!value) return false;
        const cleaned = value.replace(/\D/g, "");
        return cleaned.length >= 10 && cleaned.length <= 15;
      }),
    date_of_birth: Yup.date().required("Date of Birth is required"),
    bio: Yup.string().max(500, "Bio must be 500 characters or less")
      .required("Bio is required")
      .min(10, "Bio must be at least 10 characters"),
    latitude: Yup.number().nullable(),
    longitude: Yup.number().nullable(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        userId: auth.user_id,
        ...values,
      };
      const res = await dispatch(
        instructorUpdateProfileAPI({ payload, token: auth.token })
      ).unwrap();

      if (res.success) {
        toast.success(res.message, {
          position: "top-center",
        });
        dispatch(getLoginResponseAPI({ token: auth.token }))
        navigate("/instructor-add-education");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong", {
        position: "top-center",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return <div>Error loading Google Maps API</div>;
  }

  if (!isLoaded) {
    return <DottedLoading />;
  }

  return (
    <div className="d-flex flex-column flex-grow-1 justify-content-center px-15">
      <div className="text-center mb-20">
        <h1 className="ah-heading-02 fw-semibold mb-3">Setup Your Profile</h1>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => {
          // const phoneError =
          //   values.phone_number && !isValidPhoneNumber(values.phone_number)
          //     ? "Phone number is invalid"
          //     : undefined;
          return (
            <Form
              action="#"
              className="bg-white auth-form-holder mx-auto px-20 px-sm-30 px-md-40 pt-40 pt-md-50 pb-30"
            >
              <div className="mb-12 mb-md-16">
                <label
                  htmlFor="full_name"
                  className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                >
                  Name
                </label>
                <Field
                  name="full_name"
                  type="text"
                  className="form-control"
                  placeholder="Name"
                />
                <ErrorMessage
                  name="full_name"
                  component="div"
                  className="text-danger error"
                />
              </div>
              <PlaceAutocomplete setFieldValue={setFieldValue} />
              <div className="mb-12 mb-md-16">
                <label
                  htmlFor="phone_number"
                  className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                >
                  Phone Number
                </label>
                <div>
                  <PhoneInput
                    country={defaultCountry}
                    value={values.phone_number}
                    onChange={(value) =>
                      setFieldValue(
                        "phone_number",
                        "+" + value.replace(/^\+/, "")
                      )
                    }
                    enableSearch={true}
                    countryCodeEditable={false}
                    inputProps={{
                      name: "phone_number",
                      required: true,
                      className: "form-control",
                    }}
                  />
                </div>
                <ErrorMessage
                  name="phone_number"
                  component="div"
                  className="text-danger error"
                />
              </div>
              <div className="mb-12 mb-md-16">
                <label
                  htmlFor="date_of_birth"
                  className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                >
                  Date of Birth
                </label>
                <DatePicker
                  selected={
                    values.date_of_birth ? new Date(values.date_of_birth) : null
                  }
                  onChange={(date) =>
                    setFieldValue(
                      "date_of_birth",
                      date ? format(date, "MM-dd-yyyy") : ""
                    )
                  }
                  maxDate={today}
                  dateFormat="MM-dd-yyyy"
                  className="form-control"
                  placeholderText="MM-DD-YYYY"
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  onKeyDown={(e) => e.preventDefault()}
                />
                <ErrorMessage
                  name="date_of_birth"
                  component="div"
                  className="text-danger error"
                />
              </div>
              <div className="mb-12 mb-md-16">
                <label
                  htmlFor="bio"
                  className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                >
                  Bio
                </label>
                <Field
                  name="bio"
                  as="textarea"
                  className="form-control"
                  placeholder="Tell us about yourself"
                  rows="4"
                />
                <ErrorMessage
                  name="bio"
                  component="div"
                  className="text-danger error"
                />
              </div>
              <div className="pt-12 mb-18 mb-md-24">
                <CustomButton
                  type="submit"
                  label={"Next"}
                  classes="btn btn-primary fw-bold w-100 mb-10"
                  disabled={isSubmitting}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
